import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./PrivacyPage.css";

const CloseIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#003049" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);

export default function PrivacyPage({ onBack }) {
  const navigate = useNavigate();
  
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleClose = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/");
    }
  };

  return (
    <div className="privacy-page">
      <div className="privacy-header">
        <div className="privacy-header-spacer"></div>
        <h2 className="privacy-header-title">Privacy Policy</h2>
        <button onClick={handleClose} className="privacy-close-btn" aria-label="Close">
            <CloseIcon />
        </button>
      </div>

      <div className="privacy-content">
        <h1 className="privacy-doc-title">WorldofNABI | PRIVACY POLICY</h1>
        <p className="privacy-meta"><strong>Effective Date:</strong> June 13 2023</p>

        <h3 className="privacy-section-title">INTRODUCTION</h3>
        <p>The Privacy Policy (“Policy”), along with the Terms of Service, governs your use of the app and website application provided by WorldofNABI Private Limited (“NABI, NABI Health, our, us, we”).</p>
        <p>This Policy explains how NABI uses, protects and shares any data you provide when you use NABI’s app and website. We are committed to providing clear disclosures on your data use and protecting your privacy.</p>
        <p>NABI provides a mobile and website application (“app and/or website”) focused on emotional well-being. The app/website offers preventive and supportive care through volunteer support, online therapy, and other self-reflection tools. Our website provides additional information about the features, relevant context about using our app/website and other material that supports your emotional well-being journey.</p>

        <h3 className="privacy-section-title">DATA BEING COLLECTED</h3>
        <p>We may collect the following data depending on your use of the app and website:</p>
        <ul className="privacy-list">
            <li>Details about your profile, including your name, password, email, gender and date of birth.</li>
            <li>Information about your emotional health, including your feelings of the day, details in your virtual diary entries and reasons for your emotions.</li>
            <li>Your sign-in history.</li>
            <li>Your chats with our volunteers and therapists.</li>
            <li>Your location at the time of registration.</li>
            <li>If you consent to its collection, additional data such as your phone number, address book, educational qualifications, occupation, city/country of residence, preferences as to volunteers and therapists.</li>
            <li>Payment transaction information when you use payment gateways integrated with our app/website.</li>
            <li>Analytics and logs data for monitoring crashes and bugs in our app and website and improving the overall experience.</li>
            <li>Persistent cookies (to remember your audio and video preferences for chats with volunteers and therapists).</li>
        </ul>
        <p>Please only provide information that personally identifies you if requested explicitly during registration or other relevant processes on our app and website.</p>

        <h3 className="privacy-section-title">DATA USES</h3>
        <p>NABI’s primary aim is to support and help your journey of improving their emotional well-being. To that end, NABI will ensure that your data is protected and provided to NABI’s team (including our director, volunteers, therapists and NABI’s technical members) only to facilitate and enhance your experience.</p>
        <ul className="privacy-list">
            <li>We will use your data to provide unique, personalized and customised services based on your requirements for your well-being.</li>
            <li>We will use your data to improve and secure our app and website.</li>
            <li>We will use your data to provide information about our services, any new features being developed and changes to our services. (We may also periodically notify you of promotional materials that you may find relevant).</li>
            <li>We may deploy machine learning tools to understand the data you provide to us and to help improve our volunteer services.</li>
            <li>We may use your data to customize the app and website according to your preferences.</li>
            <li>We may use your data for research, including ways to improve emotional support services.</li>
            <li>We may use your data to comply with any legal requirements under applicable laws, including disclosure to law enforcement agencies.</li>
            <li>We may also use your data to carry out contractual obligations in the Terms of Service.</li>
        </ul>

        <h3 className="privacy-section-title">SHARING OF DATA WITH THIRD PARTIES</h3>
        <p>To provide emotional support services, NABI shares your data with its team of volunteers, therapists and technical members who help run our app and website on a need-to-know basis. Outside of NABI, we may also share your data with:</p>
        <ul className="privacy-list">
            <li>Service providers, including those that help us provide the app and website and support our cloud storage and processing requirements.</li>
            <li>Partners, including:
                <ul className="privacy-sub-list">
                    <li>Those who help us with analytics and measurement services.</li>
                    <li>Those that help provide machine learning features with the app.</li>
                </ul>
            </li>
            <li>We will ensure that only trusted partners’ services are integrated into the app.</li>
            <li>Government and law enforcement agencies when required under our legal compliances.</li>
        </ul>

        <h3 className="privacy-section-title">TRANSFERS OF DATA</h3>
        <p>We use service providers outside the country to store and process your data. We may transfer your data outside the country as part of the use of such services.</p>

        <h3 className="privacy-section-title">RETENTION OF DATA</h3>
        <p>We only retain your data for as long as necessary to fulfil the purposes for which it was collected, for durations required by applicable laws, and for backups. We will ensure your data is removed from our systems (or anonymised) as soon as it is no longer needed.</p>

        <h3 className="privacy-section-title">MANAGING OR DELETING DATA</h3>
        <p>You can choose to restrict the collection, use or disclosure of your data in the following ways:</p>
        <ul className="privacy-list">
            <li>You can request access to your data by contacting the email address in the “Contact Us” section.</li>
            <li>You can choose not to provide any additional information sought through the app/website, like your city/country of residence and your preferences for therapists/volunteers.</li>
            <li>You can ask us to rectify or update your data by contacting the email address in the “Contact Us” section.</li>
            <li>You can restrict the use of your data by deleting your profile.</li>
        </ul>

        <h3 className="privacy-section-title">CHANGES TO POLICY</h3>
        <p>We will notify you of any material changes to the Policy. You should check this page occasionally to ensure you are updated on any non-material changes to the Policy and if you are happy with these changes.</p>

        <h3 className="privacy-section-title">CONTACT US</h3>
        <p>You can reach us at <a href="mailto:reachout@nabi.health" className="privacy-link">reachout@nabi.health</a> for any questions about the Policy, your use of the app or website, or any other concerns.</p>
      </div>
    </div>
  );
}